import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, Building2, Radar, ChevronRight, ClipboardList } from 'lucide-react';
import { subscribeToMarketingContent } from '../utils/marketingService';
import { subscribeMarketingReports, subscribeMarketingOpportunityReports } from '../utils/marketingReportsService';
import { subscribeMarketingRequests } from '../utils/marketingRequestsService';
import type { MarketingOpportunityReportListItem } from '../types';
import { fmtReportDateLabel, localReportDateKey } from '../utils/marketingReportDates';
import { pickPreferredOpportunityReportId } from '../utils/marketingOpportunityReports';

const CARDS = [
  {
    to: '/marketing-hub/content',
    title: 'Content Pipeline',
    description: 'Track podcast episodes and marketing content through production stages.',
    icon: Clapperboard,
    accent: '#1D4ED8',
  },
  {
    to: '/marketing-hub/competitor-briefing',
    title: 'Competitor Briefing',
    description: 'Daily competitor scan — updates, SEO themes, GBP watch, and market trends.',
    icon: Building2,
    accent: '#7C3AED',
  },
  {
    to: '/marketing-hub/opportunity-radar',
    title: 'Opportunity Radar',
    description: 'LCTF opportunity signals synthesized from pipeline, deals, and market context.',
    icon: Radar,
    accent: '#059669',
  },
  {
    to: '/marketing-hub/content-requests',
    title: 'Content Requests',
    description: 'Submit and track marketing content to-dos with rich comments and Slack notifications.',
    icon: ClipboardList,
    accent: '#DC2626',
  },
] as const;

const MarketingHubLander: React.FC = () => {
  const [contentCount, setContentCount] = useState<number | null>(null);
  const [requestCount, setRequestCount] = useState<number | null>(null);
  const [latestCompetitorDate, setLatestCompetitorDate] = useState<string | null>(null);
  const [latestRadarDate, setLatestRadarDate] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeToMarketingContent(rows => setContentCount(rows.length));
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeMarketingRequests(rows => setRequestCount(rows.length));
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeMarketingReports(
      rows => setLatestCompetitorDate(rows[0]?.report_date ?? null),
      () => setLatestCompetitorDate(null),
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeMarketingOpportunityReports(
      (rows: MarketingOpportunityReportListItem[]) => {
        const preferredId = pickPreferredOpportunityReportId(rows, null, true);
        const preferred = rows.find(r => r.id === preferredId);
        setLatestRadarDate(preferred?.report_date ?? rows[0]?.report_date ?? null);
      },
      () => setLatestRadarDate(null),
    );
    return unsub;
  }, []);

  function metaForCard(to: string): string {
    if (to.includes('content-requests') && requestCount !== null) {
      return `${requestCount} request${requestCount === 1 ? '' : 's'}`;
    }
    if (to.endsWith('/content') && contentCount !== null) {
      return `${contentCount} item${contentCount === 1 ? '' : 's'} in pipeline`;
    }
    if (to.includes('competitor-briefing')) {
      return latestCompetitorDate
        ? `Latest: ${fmtReportDateLabel(latestCompetitorDate)}`
        : 'No briefings yet';
    }
    if (to.includes('opportunity-radar')) {
      if (!latestRadarDate) return 'No radar reports yet';
      const today = localReportDateKey();
      if (latestRadarDate === today) {
        return `Today: ${fmtReportDateLabel(latestRadarDate)}`;
      }
      return `Latest: ${fmtReportDateLabel(latestRadarDate)} (not today)`;
    }
    return '';
  }

  return (
    <div className="marketing-hub-lander">
      <header className="marketing-hub-lander-header">
        <h1>Marketing Hub</h1>
        <p>Content production, competitor intelligence, and opportunity radar in one place.</p>
      </header>

      <div className="marketing-hub-lander-grid">
        {CARDS.map(card => {
          const Icon = card.icon;
          return (
            <Link key={card.to} to={card.to} className="mh-lander-card">
              <div className="mh-lander-card-icon" style={{ color: card.accent }}>
                <Icon size={28} strokeWidth={1.75} />
              </div>
              <div className="mh-lander-card-body">
                <h2>{card.title}</h2>
                <p>{card.description}</p>
                <span className="mh-lander-card-meta">{metaForCard(card.to)}</span>
              </div>
              <ChevronRight size={18} className="mh-lander-card-chevron" aria-hidden />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default MarketingHubLander;
